/** Phase-transition effects for the welcome plan tour (activate / slot / duration). */
import { useEffect } from 'react';
import { isUnscheduledCandidate } from '@/features/tasks/utils/views/filtersViewUtils';
import { isOnboardingWelcomePlanTourPending } from '@/features/onboarding/utils/onboardingWelcomePlanSession';
import { DEFAULT_TASK_DURATION_MINS } from '@/config/constants';

/**
 * @param {Object} params
 * @param {boolean} params.active
 * @param {(v: boolean) => void} params.setActive
 * @param {import('@/features/onboarding/hooks/useOnboardingWelcomePlanTour').WelcomePlanPhase} params.phase
 * @param {(p: import('@/features/onboarding/hooks/useOnboardingWelcomePlanTour').WelcomePlanPhase) => void} params.setPhase
 * @param {React.MutableRefObject<string | null>} params.seenSlotHighlightRef
 * @param {React.MutableRefObject<string | null>} params.slotTargetTaskIdRef
 * @param {React.MutableRefObject<boolean>} params.slotSkippedRef
 * @param {React.MutableRefObject<number | null>} params.durationBaselineRef
 * @param {(v: boolean) => void} params.setAddTaskHighlightUsed
 * @param {string | null} params.slotHighlightTaskId
 * @param {import('@/types').Task[]} params.unscheduledTasks
 * @param {() => void} params.enterInvite
 * @param {() => void} params.enterDuration
 * @param {(id: string | null) => import('@/types').Task | null} params.resolveTaskById
 * @param {Map<string, import('@/types').Task>} params.tasksMap
 * @param {import('@/types').Task[]} params.nextActions
 */
export function useOnboardingWelcomePlanTourEffects({
  active,
  setActive,
  phase,
  setPhase,
  seenSlotHighlightRef,
  slotTargetTaskIdRef,
  slotSkippedRef,
  durationBaselineRef,
  setAddTaskHighlightUsed,
  slotHighlightTaskId,
  unscheduledTasks,
  enterInvite,
  enterDuration,
  resolveTaskById,
  tasksMap,
  nextActions,
}) {
  useEffect(() => {
    if (!active && isOnboardingWelcomePlanTourPending()) {
      setActive(true);
      setPhase('slot');
      seenSlotHighlightRef.current = null;
      setAddTaskHighlightUsed(false);
      slotSkippedRef.current = false;
    }
  }, [
    active,
    setActive,
    setPhase,
    seenSlotHighlightRef,
    setAddTaskHighlightUsed,
    slotSkippedRef,
  ]);

  // Lock the first slot highlight; advance to more once that task is scheduled.
  useEffect(() => {
    if (!active || phase !== 'slot') {
      return;
    }
    if (slotHighlightTaskId && !seenSlotHighlightRef.current) {
      seenSlotHighlightRef.current = slotHighlightTaskId;
      slotTargetTaskIdRef.current = slotHighlightTaskId;
    }
    const seenId = seenSlotHighlightRef.current;
    if (!seenId) {
      return;
    }
    const stillUnscheduled = (unscheduledTasks ?? []).some(
      (task) => task.id === seenId && isUnscheduledCandidate(task),
    );
    if (!stillUnscheduled) {
      seenSlotHighlightRef.current = null;
      if (slotSkippedRef.current) {
        enterInvite();
      } else {
        enterDuration();
      }
    }
  }, [
    active,
    phase,
    slotHighlightTaskId,
    unscheduledTasks,
    enterInvite,
    enterDuration,
    seenSlotHighlightRef,
    slotTargetTaskIdRef,
    slotSkippedRef,
  ]);

  useEffect(() => {
    if (!active || phase !== 'duration') {
      return;
    }
    const targetId = slotTargetTaskIdRef.current;
    const baseline = durationBaselineRef.current;
    if (!targetId || baseline === null) {
      return;
    }
    const task = resolveTaskById(targetId);
    const currentDuration = task?.duration ?? DEFAULT_TASK_DURATION_MINS;
    if (currentDuration !== baseline) {
      enterInvite();
    }
  }, [
    active,
    phase,
    tasksMap,
    unscheduledTasks,
    nextActions,
    enterInvite,
    resolveTaskById,
    slotTargetTaskIdRef,
    durationBaselineRef,
  ]);
}
